import { stripHtml } from './utils';

/**
 * Utilidades para el campo descripción de los lesson items, que pasó de un `<TextArea>`
 * a Froala. Conviven dos formatos: las descripciones heredadas son texto plano (con los
 * saltos de línea que introdujo el TextArea) y las nuevas son HTML.
 */

const EDITOR_WRAPPER_TAGS_REGEX = /<\/?(?:p|div|br)\b[^>]*>/gi;
const HTML_TAG_REGEX = /<[a-z!/]/i;

/**
 * Indica si el valor contiene marcado HTML.
 * @param {string} html - HTML o texto plano
 * @returns {boolean} True si hay al menos una etiqueta
 */
export function hasHtmlMarkup(html) {
  return HTML_TAG_REGEX.test(String(html ?? ''));
}

/**
 * Indica si el marcado se limita a los envoltorios de bloque que añade el editor
 * (`<p>`, `<div>`, `<br>`), es decir, si no hay formato real del usuario.
 * @param {string} html - HTML a inspeccionar
 * @returns {boolean} True si solo hay envoltorios de bloque
 */
export function hasOnlyEditorWrapperMarkup(html) {
  return !hasHtmlMarkup(String(html ?? '').replaceAll(EDITOR_WRAPPER_TAGS_REGEX, ''));
}

/**
 * Texto comparable entre la descripción heredada y la que emite el editor al montar.
 * Las etiquetas de bloque se sustituyen por un espacio antes de quitar el marcado,
 * porque `<p>a</p><p>b</p>` y `a\nb` son el mismo texto para el usuario; después se
 * colapsa el espacio en blanco (`\s` incluye el ` ` de los `&nbsp;` que decodifica
 * `stripHtml`).
 * @param {string} html - HTML o texto plano
 * @returns {string} Texto normalizado para comparar
 */
export function toComparableText(html) {
  return stripHtml(String(html ?? '').replaceAll(EDITOR_WRAPPER_TAGS_REGEX, ' '))
    .replaceAll(/\s+/g, ' ')
    .trim();
}

/**
 * Convierte una descripción heredada en texto plano al HTML que espera el editor,
 * preservando los saltos de línea del antiguo TextArea. Si ya viene con marcado se
 * devuelve tal cual.
 * @param {string} description - Descripción guardada
 * @returns {string} Modelo inicial para el editor
 */
export function toEditorModel(description = '') {
  if (!description) return '';
  if (hasHtmlMarkup(description)) return description;
  return description.replaceAll('\n', '<br>');
}

/**
 * Indica si la diferencia entre ambos valores es SOLO la normalización que el editor
 * aplica al montar (`hola` -> `<p>hola</p>`, `a\nb` -> `<p>a<br>b</p>`), que emite su
 * `onChange` sin que el usuario toque nada.
 * @param {string} initialDescription - Descripción guardada
 * @param {string} currentDescription - Descripción que tiene el formulario
 * @returns {boolean} True si el editor solo ha normalizado
 */
export function isEditorNormalization(initialDescription, currentDescription) {
  return (
    !hasHtmlMarkup(initialDescription) &&
    hasOnlyEditorWrapperMarkup(currentDescription) &&
    toComparableText(currentDescription) === toComparableText(initialDescription)
  );
}

/**
 * Indica si el usuario ha cambiado realmente la descripción. Importa porque la
 * descripción se escribe en el lesson item ORIGINAL, que se propaga a todos los cursos
 * que lo referencian: enviarla por una normalización del editor la pisaría sin que
 * nadie la haya tocado.
 * @param {string} initialDescription - Descripción guardada
 * @param {string} currentDescription - Descripción que tiene el formulario
 * @returns {boolean} True si hay un cambio real del usuario
 */
export function hasDescriptionChanged(initialDescription = '', currentDescription = '') {
  if (currentDescription === initialDescription) return false;
  return !isEditorNormalization(initialDescription, currentDescription);
}

/**
 * Descripción tal y como debe persistirse. Froala emite envoltorios vacíos
 * (`<p><br></p>`) cuando el docente deja el campo en blanco: si se guardaran, un
 * `{description && ...}` pintaría un bloque vacío y Configuración general abriría
 * con una descripción "no vacía" que el usuario no ve.
 * @param {string} description - Valor que emite el editor
 * @returns {string} El HTML, o cadena vacía si no tiene texto
 */
export function toPersistedDescription(description = '') {
  if (!description) return '';
  return stripHtml(description) ? description : '';
}
